import React, { createContext, useContext, useEffect, useState } from 'react';
import en from './locales/en/translation.json';
import es from './locales/es/translation.json';
import ptBR from './locales/pt-BR/translation.json';
import zh from './locales/zh/translation.json';
import hi from './locales/hi/translation.json';
import fr from './locales/fr/translation.json';
import ar from './locales/ar/translation.json';
import bn from './locales/bn/translation.json';
import ru from './locales/ru/translation.json';
import ur from './locales/ur/translation.json';
import he from './locales/he/translation.json';
import ja from './locales/ja/translation.json';
import ko from './locales/ko/translation.json';
import pnb from './locales/pnb/translation.json';
import mr from './locales/mr/translation.json';

const resources = {
  en,
  es,
  'pt-BR': ptBR,
  zh,
  hi,
  fr,
  ar,
  bn,
  ru,
  ur,
  he,
  ja,
  ko,
  pnb,
  mr,
};

export const supportedLanguages = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'zh', label: '中文' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'fr', label: 'Français' },
  { value: 'ar', label: 'العربية' },
  { value: 'bn', label: 'বাংলা' },
  { value: 'ru', label: 'Русский' },
  { value: 'ur', label: 'اردو' },
  { value: 'he', label: 'עברית' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'pnb', label: 'ਪੰਜਾਬੀ' },
  { value: 'mr', label: 'मराठी' },
];

const isSupportedLanguage = (lang) => Boolean(resources[lang]);
const rtlLanguages = new Set(['ar', 'ur', 'he']);

const I18nContext = createContext({
  language: 'en',
  changeLanguage: () => {},
  t: (key) => key,
});

export const I18nProvider = ({ children }) => {
  const stored = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
  const [language, setLanguage] = useState(isSupportedLanguage(stored) ? stored : 'en');

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      document.documentElement.dir = rtlLanguages.has(language) ? 'rtl' : 'ltr';
    }
    if (typeof window !== 'undefined' && localStorage.getItem('language') !== language) {
      localStorage.setItem('language', language);
    }
  }, [language]);

  const t = (key, vars = {}) => {
    const fallback = typeof vars === 'string' ? vars : key;
    const replacements = vars && typeof vars === 'object' && !Array.isArray(vars) ? vars : {};
    const keys = key.split('.');
    let result = resources[language];
    for (const k of keys) {
      result = result?.[k];
    }
    if (typeof result !== 'string') {
      result = resources.en;
      for (const k of keys) {
        result = result?.[k];
      }
    }
    if (typeof result === 'string') {
      Object.keys(replacements).forEach((v) => {
        result = result.replace(`{{${v}}}`, replacements[v]);
      });
    }
    return result || fallback;
  };

  const changeLanguage = (lang) => {
    if (!isSupportedLanguage(lang)) return;
    setLanguage(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', lang);
    }
  };

  return (
    <I18nContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => useContext(I18nContext);
